import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AppStatusBadge, StatusBadge } from "@/components/Badges";
import { PaymentBadge } from "@/components/Badges";
import ScorecardView from "@/components/ScorecardView";
import { adminApi, contentApi, certApi, downloadAuthedFile } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileText, Image as ImageIcon, Check, X, Info, ShieldAlert, Award, Save, History, Download, Sparkles, Bot, CheckCircle2, AlertTriangle } from "lucide-react";

const EVIDENCE = ["No evidence", "Policy only", "Procedure available", "Records available", "Implemented", "Document reviewed"];
const RISK = ["Low Risk", "Medium Risk", "High Risk", "Critical Risk"];

const AdminReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appData, setAppData] = useState(null);
  const [framework, setFramework] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState([]);
  const [comments, setComments] = useState("");
  const [decisionComment, setDecisionComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [cert, setCert] = useState(null);
  const [issue, setIssue] = useState({ level: "", expiry_months: "12", reviewer: "WECH-RB Certification Body" });

  const load = async () => {
    const [a, f] = await Promise.all([adminApi.getApplication(id), contentApi.framework()]);
    setAppData(a.data);
    setFramework(f.data);
    const admin = a.data.admin_review;
    const base = admin?.final_category_scores || a.data.ai_review?.category_scores || f.data.categories.map((c) => ({ key: c.key, name: c.name, score: 0, evidence_level: "Policy only", risk_rating: "Medium Risk" }));
    setScores(base.map((c) => ({ ...c })));
    setComments(admin?.reviewer_comments || "");
    setIssue((s) => ({ ...s, level: admin?.final_level || a.data.ai_review?.certification_level || "" }));
    if (a.data.certificate_id) {
      try { const { data } = await certApi.get(a.data.certificate_id); setCert(data); } catch {}
    }
  };

  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, [id]);

  const scopeKeys = (appData && appData.scope_type === "category" && (appData.selected_categories || []).length)
    ? appData.selected_categories
    : (framework?.categories || []).map((c) => c.key);
  const scopeSet = new Set(scopeKeys);
  const isIn = (c) => (typeof c.in_scope === "boolean" ? c.in_scope : scopeSet.has(c.key));
  const inScopeScores = scores.filter(isIn);
  const overall = inScopeScores.length ? Math.round(inScopeScores.reduce((s, c) => s + Number(c.score || 0), 0) / inScopeScores.length) : 0;

  const setScore = (key, patch) => setScores((arr) => arr.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const runAiReview = async () => {
    setAiRunning(true);
    try {
      await adminApi.runAiReview(id);
      const start = Date.now();
      const TIMEOUT_MS = 210000; // 3.5 minutes
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - start > TIMEOUT_MS) { toast.error("AI review is taking longer than expected. Try again."); break; }
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await adminApi.aiReviewStatus(id);
        if (data.status === "done" || data.has_review) { toast.success("AI gap review complete"); await load(); break; }
        if (data.status === "error") { toast.error(data.error || "AI review failed"); await load(); break; }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not run AI review");
    } finally { setAiRunning(false); }
  };

  const saveScores = async () => {
    setBusy(true);
    try {
      await adminApi.adjustScores(id, { category_scores: scores.map((c) => ({ key: c.key, score: Number(c.score), evidence_level: c.evidence_level, risk_rating: c.risk_rating })), reviewer_comments: comments });
      toast.success("Scores saved");
      await load();
    } catch { toast.error("Could not save scores"); } finally { setBusy(false); }
  };

  const decide = async (decision) => {
    setBusy(true);
    try {
      await adminApi.decision(id, { decision, comment: decisionComment });
      toast.success("Decision recorded");
      setDecisionComment("");
      await load();
    } catch { toast.error("Could not record decision"); } finally { setBusy(false); }
  };

  const issueCert = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.issueCertificate(id, { level: issue.level || undefined, expiry_months: Number(issue.expiry_months), reviewer: issue.reviewer });
      toast.success(`Certificate ${data.certificate_id} issued`);
      await load();
    } catch { toast.error("Could not issue certificate"); } finally { setBusy(false); }
  };

  const changeCertStatus = async (status) => {
    setBusy(true);
    try { await adminApi.certStatus(appData.certificate_id, status); toast.success(`Certificate ${status.toLowerCase()}`); await load(); }
    catch { toast.error("Could not update certificate"); } finally { setBusy(false); }
  };

  if (loading || !appData) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>;

  return (
    <div className="wechr-section">
      <div className="wechr-container">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <button onClick={() => navigate("/admin")} className="text-sm text-wgreen-700 hover:underline">← Back to dashboard</button>
            <h1 className="mt-1 font-heading text-2xl font-semibold text-navy-900">{appData.company_name}</h1>
            <p className="text-sm text-[var(--wechr-muted-text)]">{appData.industry} · {appData.country} · {appData.num_employees} employees · Requested {appData.requested_level}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={async () => { try { await downloadAuthedFile(adminApi.scorecardPdfPath(id), `WECH-RB-Scorecard-${appData.company_name}.pdf`); } catch { toast.error("Could not download report"); } }} data-testid="admin-download-scorecard"><Download className="mr-1 h-4 w-4" /> Report</Button>
            {appData.payment && <PaymentBadge status={appData.payment.status} testId="admin-payment-badge" />}
            <AppStatusBadge status={appData.status} />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left: review content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="ai">
              <TabsList className="flex-wrap">
                <TabsTrigger value="ai">AI Review</TabsTrigger>
                <TabsTrigger value="questionnaire">Questionnaire</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="audit" data-testid="admin-audit-tab">Audit Trail</TabsTrigger>
              </TabsList>

              <TabsContent value="ai" className="mt-4">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--wechr-border)] bg-[var(--wechr-subtle)] p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-wgreen-100"><Bot className="h-5 w-5 text-wgreen-700" /></div>
                    <div>
                      <p className="font-heading text-sm font-semibold text-navy-900">AI-assisted gap review</p>
                      <p className="text-xs text-[var(--wechr-muted-text)]">Analyzes uploaded documents against the Code of Conduct. Reviewer-only tool; not shown to applicants.</p>
                    </div>
                  </div>
                  <Button className="btn-green" size="sm" onClick={runAiReview} disabled={aiRunning} data-testid="admin-run-ai-review-button">
                    {aiRunning ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing…</> : <><Sparkles className="mr-2 h-4 w-4" /> {appData.ai_review ? "Re-run AI review" : "Run AI review"}</>}
                  </Button>
                </div>
                {appData.ai_review ? <ScorecardView review={appData.ai_review} preliminary /> : <p className="text-sm text-[var(--wechr-muted-text)]">No AI review yet. Click "Run AI review" to generate a preliminary scorecard from the uploaded documents.</p>}
              </TabsContent>

              <TabsContent value="questionnaire" className="mt-4 space-y-3">
                {Object.keys(appData.questionnaire || {}).length === 0 ? <p className="text-sm text-[var(--wechr-muted-text)]">No questionnaire answers.</p> :
                  Object.entries(appData.questionnaire).map(([qid, a]) => (
                    <Card key={qid}><CardContent className="p-4"><p className="font-mono text-xs text-[var(--wechr-muted-text)]">{qid}</p><p className="font-medium text-navy-900">{a.value}</p>{a.notes && <p className="mt-1 text-sm text-[var(--wechr-muted-text)]">{a.notes}</p>}</CardContent></Card>
                  ))}
              </TabsContent>

              <TabsContent value="documents" className="mt-4 space-y-3">
                {(appData.documents || []).length === 0 ? <p className="text-sm text-[var(--wechr-muted-text)]">No documents uploaded.</p> : (
                  <>
                    <div className="flex items-center justify-between rounded-xl border border-[var(--wechr-border)] bg-[var(--wechr-subtle)] px-4 py-3">
                      <p className="text-sm text-navy-800"><span className="font-semibold">{appData.documents.length}</span> document{appData.documents.length === 1 ? "" : "s"} uploaded</p>
                      <Button size="sm" variant="outline" onClick={async () => { try { await downloadAuthedFile(adminApi.docsZipPath(id), `WECH-RB-Documents-${appData.company_name}.zip`); } catch { toast.error("Could not download documents"); } }} data-testid="admin-download-all-docs"><Download className="mr-1 h-4 w-4" /> Download all (ZIP)</Button>
                    </div>
                    {appData.documents.map((d) => (
                      <Card key={d.id}><CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          {d.is_image ? <ImageIcon className="h-4 w-4 shrink-0 text-wgreen-700" /> : <FileText className="h-4 w-4 shrink-0 text-wgreen-700" />}
                          <span className="font-medium text-navy-900">{d.filename}</span>
                          <span className="rounded-full bg-[var(--wechr-subtle)] px-2 py-0.5 text-xs text-navy-700">{d.category_name}</span>
                          {d.evidence_type && <span className="rounded-full bg-wgreen-100 px-2 py-0.5 text-xs text-wgreen-700">{d.evidence_type}</span>}
                          <Button size="sm" variant="outline" className="ml-auto" onClick={async () => { try { await downloadAuthedFile(adminApi.docDownloadPath(id, d.id), d.filename); } catch { toast.error("Could not download file"); } }} data-testid={`admin-download-doc-${d.id}`}><Download className="mr-1 h-4 w-4" /> Download</Button>
                        </div>
                        {d.text_preview && <p className="mt-2 max-h-32 overflow-y-auto rounded bg-[var(--wechr-subtle)] p-2 text-xs text-navy-800">{d.text_preview}</p>}
                        {d.is_image && <p className="mt-2 text-xs text-[var(--wechr-muted-text)]">Image document — analyzed via AI vision.</p>}
                      </CardContent></Card>
                    ))}
                  </>
                )}
              </TabsContent>

              <TabsContent value="profile" className="mt-4">
                <Card><CardContent className="grid gap-3 p-6 sm:grid-cols-2 text-sm">
                  {[["Company", appData.company_name], ["Website", appData.website], ["Contact", appData.contact_person], ["Email", appData.contact_email], ["Country", appData.country], ["Industry", appData.industry], ["Employees", appData.num_employees], ["Facility", appData.site_type], ["Customer industries", appData.customer_industries], ["Requested level", appData.requested_level]].map(([k, v]) => (
                    <div key={k}><p className="text-xs uppercase tracking-wide text-[var(--wechr-muted-text)]">{k}</p><p className="font-medium text-navy-900">{v || "—"}</p></div>
                  ))}
                </CardContent></Card>
              </TabsContent>

              <TabsContent value="audit" className="mt-4">
                <Card><CardContent className="p-6">
                  {(appData.audit_log || []).length === 0 ? (
                    <p className="text-sm text-[var(--wechr-muted-text)]">No audit entries yet.</p>
                  ) : (
                    <ol className="relative space-y-5 border-l border-[var(--wechr-border)] pl-6" data-testid="admin-audit-list">
                      {[...appData.audit_log].reverse().map((e) => (
                        <li key={e.id} className="relative">
                          <span className="absolute -left-[27px] flex h-4 w-4 items-center justify-center rounded-full bg-wgreen-600"><History className="h-2.5 w-2.5 text-white" /></span>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-medium text-navy-900">{e.detail}</p>
                            <span className="font-mono text-xs text-[var(--wechr-muted-text)]">{new Date(e.at).toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-[var(--wechr-muted-text)]">{e.action} · by {e.by}</p>
                          {e.changes && e.changes.length > 0 && (
                            <div className="mt-2 space-y-1 rounded-lg bg-[var(--wechr-subtle)] p-2 text-xs text-navy-800">
                              {e.changes.map((c, i) => (<div key={i}><span className="font-medium">{c.category}:</span> {c.from} → {c.to}</div>))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent></Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right: scoring + decisions */}
          <div className="space-y-6">
            {appData.payment && appData.payment.status !== "paid" && (
              <Card className="border-[rgba(154,52,18,0.35)]" data-testid="admin-payment-required-card">
                <CardContent className="flex items-start gap-3 p-4">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#9A3412]" />
                  <div className="text-sm">
                    <p className="font-semibold text-navy-900">{appData.payment.status === "processing" ? "Payment processing" : "Awaiting payment"}</p>
                    <p className="text-xs text-[var(--wechr-muted-text)]">{appData.payment.status === "processing" ? "The applicant's bank (ACH) payment is settling. Review actions unlock once it clears." : "Review decisions and certificate issuance are locked until the applicant completes payment."}</p>
                  </div>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Adjust scores</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {scores.map((c) => (
                  isIn(c) ? (
                  <div key={c.key} className="rounded-lg border border-[var(--wechr-border)] p-3" data-testid={`admin-score-row-${c.key}`}>
                    <div className="flex items-center justify-between"><Label className="text-sm">{c.name}</Label>
                      <Input type="number" min={0} max={100} value={c.score ?? 0} onChange={(e) => setScore(c.key, { score: e.target.value })} className="w-20" data-testid={`admin-score-${c.key}`} />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Select value={c.evidence_level} onValueChange={(v) => setScore(c.key, { evidence_level: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{EVIDENCE.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={c.risk_rating} onValueChange={(v) => setScore(c.key, { risk_rating: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{RISK.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  ) : (
                  <div key={c.key} className="flex items-center justify-between rounded-lg border border-dashed border-[var(--wechr-border)] bg-[var(--wechr-subtle)] p-3" data-testid={`admin-score-row-${c.key}`}>
                    <Label className="text-sm text-[var(--wechr-muted-text)]">{c.name}</Label>
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[var(--wechr-muted-text)]" data-testid={`admin-not-in-scope-${c.key}`}>Not in Scope</span>
                  </div>
                  )
                ))}
                <div className="flex items-center justify-between rounded-lg bg-navy-900 px-4 py-3 text-white">
                  <div><span className="text-sm">Overall score</span><p className="text-[11px] text-white/70">Averaged over {inScopeScores.length} in-scope categor{inScopeScores.length === 1 ? "y" : "ies"} only</p></div>
                  <span className="font-heading text-2xl font-semibold" data-testid="admin-overall-score">{overall}</span>
                </div>
                <div><Label className="mb-1 block text-sm">Reviewer comments</Label>
                  <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} data-testid="admin-review-comment" />
                </div>
                <Button className="btn-green w-full" onClick={saveScores} disabled={busy} data-testid="admin-save-scores-button"><Save className="mr-2 h-4 w-4" /> Save scores</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Decision</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Textarea placeholder="Decision comment (optional)" value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} rows={2} />
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => decide("request_info")} disabled={busy} data-testid="admin-request-info-button"><Info className="mr-1 h-4 w-4" /> Request info</Button>
                  <Button variant="outline" onClick={() => decide("enhanced_review")} disabled={busy} data-testid="admin-enhanced-review-button"><ShieldAlert className="mr-1 h-4 w-4" /> Enhanced</Button>
                  <Button className="bg-[#B42318] text-white hover:bg-[#8f1c13]" onClick={() => decide("reject")} disabled={busy} data-testid="admin-reject-button"><X className="mr-1 h-4 w-4" /> Reject</Button>
                  <Button className="btn-green" onClick={() => decide("approve")} disabled={busy} data-testid="admin-approve-button"><Check className="mr-1 h-4 w-4" /> Approve</Button>
                </div>
              </CardContent>
            </Card>

            {(() => {
              const elig = appData.eligibility || {};
              const checks = elig.checks || {};
              return (
                <Card data-testid="admin-eligibility-card" className={elig.eligible ? "border-[rgba(15,122,74,0.35)]" : "border-[rgba(180,35,24,0.35)]"}>
                  <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold">
                    {elig.eligible ? <CheckCircle2 className="h-4 w-4 text-wgreen-700" /> : <AlertTriangle className="h-4 w-4 text-[#B42318]" />} Certification eligibility
                  </CardTitle></CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${elig.eligible ? "bg-wgreen-100 text-wgreen-700" : "bg-[#FEE4E2] text-[#B42318]"}`} data-testid="admin-eligibility-status">
                      {elig.eligible ? "Eligible for certification" : "Not eligible yet"}
                    </div>
                    <ul className="space-y-1.5 text-xs text-navy-800">
                      <li className="flex items-center justify-between"><span>Overall score ≥ {checks.min_overall_score}</span><span className={`font-semibold ${elig.overall_score >= (checks.min_overall_score || 70) ? "text-wgreen-700" : "text-[#B42318]"}`}>{elig.overall_score ?? 0}</span></li>
                      <li className="flex items-center justify-between"><span>Final scores saved by reviewer</span><span className={`font-semibold ${checks.has_final_scores ? "text-wgreen-700" : "text-[#B42318]"}`}>{checks.has_final_scores ? "Yes" : "No"}</span></li>
                      <li className="flex items-center justify-between"><span>Documents uploaded (in scope)</span><span className={`font-semibold ${checks.required_docs_uploaded === checks.required_docs_total ? "text-wgreen-700" : "text-[#9A3412]"}`}>{checks.required_docs_uploaded ?? 0} / {checks.required_docs_total ?? 0}</span></li>
                    </ul>
                    {!elig.eligible && (elig.reasons || []).length > 0 && (
                      <div className="rounded-lg bg-[#FEE4E2] p-2 text-xs text-[#7A1710]" data-testid="admin-eligibility-reasons">
                        <p className="mb-1 font-semibold">Blocking issues:</p>
                        <ul className="list-disc space-y-0.5 pl-4">{elig.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                      </div>
                    )}
                    {(elig.warnings || []).length > 0 && (
                      <div className="rounded-lg bg-[#FFF4E5] p-2 text-xs text-[#9A3412]" data-testid="admin-eligibility-warnings">
                        <p className="mb-1 flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Warnings (do not block certification):</p>
                        <ul className="list-disc space-y-0.5 pl-4">{elig.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                      </div>
                    )}
                    <p className="text-[11px] text-[var(--wechr-muted-text)]">Certification requires a minimum overall score, saved reviewer scores, payment, and your approval. Evidence level and missing documents are shown as warnings only and do not block certification. Out-of-scope categories are marked "Not in Scope" and excluded from the score.</p>
                  </CardContent>
                </Card>
              );
            })()}

            <Card>
              <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><Award className="h-4 w-4 text-gold-600" /> Certificate</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {cert ? (
                  <>
                    <div className="flex items-center justify-between"><span className="font-mono text-sm text-navy-900">{cert.certificate_id}</span><StatusBadge status={cert.effective_status} /></div>
                    <p className="text-sm text-navy-800">{cert.level} · {cert.overall_score}/100 · expires {cert.expiry_date}</p>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/certificate/${cert.certificate_id}`}><Button size="sm" variant="outline">View</Button></Link>
                      <Button size="sm" variant="outline" onClick={() => changeCertStatus("Active")} disabled={busy}>Reactivate</Button>
                      <Button size="sm" variant="outline" onClick={() => changeCertStatus("Suspended")} disabled={busy}>Suspend</Button>
                      <Button size="sm" className="bg-[#B42318] text-white hover:bg-[#8f1c13]" onClick={() => changeCertStatus("Revoked")} disabled={busy}>Revoke</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="mb-1 block text-xs">Level</Label>
                        <Select value={issue.level} onValueChange={(v) => setIssue({ ...issue, level: v })}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Auto" /></SelectTrigger>
                          <SelectContent>{["Silver Certified", "Gold Certified", "Platinum Certified"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="mb-1 block text-xs">Valid (months)</Label>
                        <Select value={issue.expiry_months} onValueChange={(v) => setIssue({ ...issue, expiry_months: v })}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{["12", "24", "36"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label className="mb-1 block text-xs">Reviewer / organization</Label><Input value={issue.reviewer} onChange={(e) => setIssue({ ...issue, reviewer: e.target.value })} /></div>
                    <Button className="btn-green w-full" onClick={issueCert} disabled={busy || !appData.eligibility?.eligible} data-testid="admin-issue-certificate-button"><Award className="mr-2 h-4 w-4" /> Issue certificate</Button>
                    {!appData.eligibility?.eligible
                      ? <p className="text-xs text-[#B42318]">Certificate is locked until all eligibility requirements above are met.</p>
                      : <p className="text-xs text-[var(--wechr-muted-text)]">Save scores first to lock in the final assessment.</p>}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReview;
